import React, {useCallback, useEffect, useRef, useState} from "react";
import {approveSiteStaff, listSiteStaff, rejectSiteStaff} from "../api/admin";

const STATUS_TABS = ["PENDING", "ACTIVE", "SUSPENDED", "ALL"];
const TONE = {ACTIVE: "green", PENDING: "amber", SUSPENDED: "grey"};

/** Builders' site staff: approve a new one so they can sign in to the OGM Agent app, or reject them. */
const SiteStaffApprovals = ({onAuthError}) => {
    const [status, setStatus] = useState("PENDING");
    const [staff, setStaff] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);
    const [confirming, setConfirming] = useState(null);

    const shownStatus = useRef(status);
    shownStatus.current = status;

    const load = useCallback(async () => {
        const wanted = shownStatus.current;
        setError("");
        try {
            const rows = await listSiteStaff(wanted);
            if (shownStatus.current === wanted) setStaff(rows);
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => { setStaff(null); load(); }, [status, load]);

    const act = async (s, fn) => {
        setBusy(s.agentId);
        setError("");
        try {
            await fn(s.agentId);
            await load();
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        } finally {
            setBusy(null);
            setConfirming(null);
        }
    };

    return (
        <div>
            <div className="brk-card__head">
                <div className="brk-filters" role="group" aria-label="Filter site staff by status">
                    {STATUS_TABS.map((s) => (
                        <button key={s} type="button" aria-pressed={status === s}
                                className={`brk-filter${status === s ? " brk-filter--on" : ""}`} onClick={() => setStatus(s)}>
                            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                        </button>
                    ))}
                </div>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {!staff ? <p className="brk-empty">Loading…</p> : staff.length === 0 ? (
                <p className="brk-empty">{status === "PENDING" ? "Nobody waiting for approval." : "No site staff with this status."}</p>
            ) : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Name</th><th>Builder</th><th>Contact</th><th>Projects</th><th>Status</th><th/></tr>
                        </thead>
                        <tbody>
                        {staff.map((s) => (
                            <tr key={s.agentId}>
                                <td className="brk-strong">{s.name}</td>
                                <td>{s.builderName || "—"}</td>
                                <td><div>{(s.mobile || "").replace(/^91/, "")}</div>{s.email && <div className="brk-hint">{s.email}</div>}</td>
                                <td className="brk-hint">{s.sites.map((x) => x.name).filter(Boolean).join(", ") || "—"}</td>
                                <td><span className={`brk-badge brk-badge--${TONE[s.status] || "grey"}`}>{s.status}</span></td>
                                <td>
                                    {confirming === s.agentId ? (
                                        <span className="brk-row-actions">
                                            <span className="brk-hint">{s.status === "ACTIVE" ? "Suspend" : "Reject"}?</span>
                                            <button type="button" className="brk-link brk-link--danger" disabled={busy === s.agentId}
                                                    onClick={() => act(s, rejectSiteStaff)}>Yes</button>
                                            <button type="button" className="brk-link" onClick={() => setConfirming(null)}>No</button>
                                        </span>
                                    ) : (
                                        <span className="brk-row-actions">
                                            {s.status !== "ACTIVE" && (
                                                <button type="button" className="brk-link" disabled={busy === s.agentId} onClick={() => act(s, approveSiteStaff)}>
                                                    {s.status === "PENDING" ? "Approve" : "Re-approve"}
                                                </button>
                                            )}
                                            {s.status !== "SUSPENDED" && (
                                                <button type="button" className="brk-link brk-link--danger" onClick={() => setConfirming(s.agentId)}>
                                                    {s.status === "ACTIVE" ? "Suspend" : "Reject"}
                                                </button>
                                            )}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SiteStaffApprovals;
